import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, CheckCircle, AlertCircle, ExternalLink } from 'lucide-react';
import { ProgressStepper, PAYMENT_STEPS } from '../components/ProgressStepper';
import { usePaymentParams } from './PaymentRouter';
import { apiUrl } from '../lib/api';

interface KycField {
  documentType: string;
  label: string;
  required: boolean;
}

type Mode = 'loading' | 'field_based' | 'redirect' | 'pending' | 'verified' | 'error';

/**
 * KycPage
 *
 * Inline identity verification, rendered directly in the checkout (the user
 * never leaves the payment page). Drives the shared-KYC endpoints:
 *   GET  /shared-kyc/kyc/requirements?countryCode=XX -> field_based | redirect
 *   POST /shared-kyc/kyc/submit  { countryCode, documents }
 *   GET  /shared-kyc/kyc/status?countryCode=XX       -> poll until VERIFIED
 *
 * Replaces a broken embed of the PouchPay widget (wrong script URL, wrong
 * global, and a `skipToStep` prop the widget never supported) that hung on a
 * spinner forever with no error path.
 */
export function KycPage() {
  const navigate = useNavigate();
  const params = usePaymentParams();

  const [mode, setMode] = useState<Mode>('loading');
  const [fields, setFields] = useState<KycField[]>([]);
  const [values, setValues] = useState<Record<string, string>>({});
  const [redirectUrl, setRedirectUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const token = params?.at || localStorage.getItem('pouchpay_token') || '';
  const countryCode = params?.countryCode || 'NG';

  // Load requirements (or short-circuit if already verified)
  useEffect(() => {
    if (!params) return;
    let cancelled = false;

    (async () => {
      if (!token) {
        setMode('error');
        setError('You need to verify your email before identity verification.');
        return;
      }

      try {
        // Already verified? Skip straight back to review.
        const statusRes = await fetch(
          apiUrl(`/shared-kyc/kyc/status?countryCode=${encodeURIComponent(countryCode)}`),
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (statusRes.ok) {
          const s = await statusRes.json();
          if (s.status === 'VERIFIED') {
            if (!cancelled) { setMode('verified'); setTimeout(() => navigate('/pay/review'), 1200); }
            return;
          }
          if (s.status === 'PENDING') {
            if (!cancelled) setMode('pending');
            return;
          }
        }

        const res = await fetch(
          apiUrl(`/shared-kyc/kyc/requirements?countryCode=${encodeURIComponent(countryCode)}`),
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.message || body.error || 'Could not load verification requirements');
        }
        const data = await res.json();
        if (cancelled) return;

        if (data.type === 'redirect' && data.redirectUrl) {
          setRedirectUrl(data.redirectUrl);
          setMode('redirect');
        } else if (Array.isArray(data.fields) && data.fields.length > 0) {
          setFields(data.fields);
          setMode('field_based');
        } else {
          throw new Error('No verification method is available for your country right now.');
        }
      } catch (err: any) {
        if (cancelled) return;
        setMode('error');
        setError(err.message || 'Could not start identity verification');
      }
    })();

    return () => { cancelled = true; };
  }, [params, token, countryCode]);

  async function checkStatus() {
    setError(null);
    try {
      const res = await fetch(
        apiUrl(`/shared-kyc/kyc/status?countryCode=${encodeURIComponent(countryCode)}`),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error('Could not check verification status');
      const data = await res.json();
      if (data.status === 'VERIFIED') {
        setMode('verified');
        setTimeout(() => navigate('/pay/review'), 1200);
      } else if (data.status === 'FAILED') {
        setMode('error');
        setError(data.failureReason || 'Verification failed. Please try again.');
      } else {
        setMode('pending');
      }
    } catch (err: any) {
      setError(err.message);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    const missing = fields.filter((f) => f.required && !values[f.documentType]?.trim());
    if (missing.length > 0) {
      setError(`Please fill in: ${missing.map((f) => f.label).join(', ')}`);
      setSubmitting(false);
      return;
    }

    try {
      const documents: Record<string, string> = {};
      for (const f of fields) {
        const v = values[f.documentType]?.trim();
        if (v) documents[f.documentType] = v;
      }

      const res = await fetch(apiUrl('/shared-kyc/kyc/submit'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ countryCode, documents })
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || body.error || 'Verification submission failed');
      }
      const data = await res.json();

      if (data.status === 'VERIFIED') {
        setMode('verified');
        setTimeout(() => navigate('/pay/review'), 1200);
      } else {
        setMode('pending');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  const shell = (children: React.ReactNode) => (
    <div className="min-h-screen bg-gray-50">
      <ProgressStepper steps={PAYMENT_STEPS} currentStep="kyc" completedSteps={['amount', 'wallet', 'auth']} />
      <div className="max-w-xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-gray-900 text-center mb-2">Identity Verification</h1>
        <p className="text-center text-gray-600 mb-8">
          Complete verification to proceed with your transaction
        </p>
        {children}
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>Why do we need this?</strong> We're required by law to verify your identity before
            processing crypto transactions. Your information is encrypted and stored securely.
          </p>
        </div>
      </div>
    </div>
  );

  if (mode === 'verified') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Identity Verified</h2>
          <p className="text-gray-600">Returning to your order…</p>
        </div>
      </div>
    );
  }

  if (mode === 'loading') {
    return shell(
      <div className="bg-white rounded-lg shadow-sm flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-brand" />
      </div>
    );
  }

  return shell(
    <div className="bg-white rounded-lg shadow-sm p-6">
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {mode === 'field_based' && (
        <form onSubmit={handleSubmit} className="space-y-4">
          {fields.map((f) => (
            <div key={f.documentType}>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {f.label}
                {f.required && <span className="text-red-500"> *</span>}
              </label>
              <input
                type="text"
                value={values[f.documentType] || ''}
                onChange={(e) => setValues({ ...values, [f.documentType]: e.target.value })}
                disabled={submitting}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand disabled:bg-gray-100"
              />
            </div>
          ))}
          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-brand text-white font-medium py-3 rounded-lg hover:opacity-90 disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {submitting ? (<><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</>) : 'Submit for verification'}
          </button>
        </form>
      )}

      {mode === 'redirect' && redirectUrl && (
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Verification for your country is handled by our verification partner. Complete it in the
            new tab, then come back and continue.
          </p>
          <a
            href={redirectUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full bg-brand text-white font-medium py-3 rounded-lg hover:opacity-90 flex items-center justify-center gap-2"
          >
            Verify my identity <ExternalLink className="w-4 h-4" />
          </a>
          <button onClick={checkStatus} className="w-full border border-gray-300 text-gray-700 font-medium py-3 rounded-lg hover:bg-gray-50">
            I've completed verification
          </button>
        </div>
      )}

      {mode === 'pending' && (
        <div className="text-center space-y-4">
          <Loader2 className="w-8 h-8 animate-spin text-brand mx-auto" />
          <div>
            <p className="font-medium text-gray-900">Verification in progress</p>
            <p className="text-sm text-gray-600">This usually takes a moment. Check again shortly.</p>
          </div>
          <button onClick={checkStatus} className="w-full border border-gray-300 text-gray-700 font-medium py-3 rounded-lg hover:bg-gray-50">
            Check status
          </button>
        </div>
      )}

      {mode === 'error' && (
        <button onClick={() => window.location.reload()} className="w-full border border-gray-300 text-gray-700 font-medium py-3 rounded-lg hover:bg-gray-50">
          Try again
        </button>
      )}
    </div>
  );
}
